// Core packages
import { Component, OnDestroy, OnInit } from "@angular/core";

// Third party packages
import { MatDialog } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { ChartDataSets, ChartOptions, ChartType } from "chart.js";
import moment from "moment";
import {
  Color, Label,
  MultiDataSet,
  PluginServiceGlobalRegistrationAndOptions
} from "ng2-charts";
import { Observable, Subscription } from "rxjs";

// Custom packages
import { Router } from "@angular/router";
import { AccessLogsService } from "src/app/modules/access-logs/access-logs.service";
import { AuthService } from "src/app/modules/auth/auth.service";
import { CaretakersService } from "src/app/modules/caretakers/caretakers.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { DeadlinesService } from "src/app/modules/deadlines/deadlines.service";
import { DocumentArchiveDocsService } from "src/app/modules/properties/document-archive-docs.service";
import { DocumentArchivesService } from "src/app/modules/properties/document-archives.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { SuppliersService } from "src/app/modules/suppliers/suppliers.service";
import { TankService } from "src/app/modules/tank/tank.service";
import { UsersService } from "src/app/modules/users/users.service";
import { UtilsService } from "src/app/modules/utils/utils.service";
import { InfoDialogComponent } from "src/app/shared/components/dialogs/info-dialog/info-dialog.component";
import { ListApiResponseV2 } from "src/app/shared/interfaces/_base/api-response.interface";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ThemeService } from "src/app/shared/services/theme.service";
import { isArchiveV3Live } from "src/app/shared/utils/archive-switch.utils";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";

@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.scss"],
})
export class HomeComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  pageIsLoaded = false;
  loggedUser: IUser;
  today = new Date();
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - turnoverDoughnut chart
  doughnutChartLabels: Label[] = [];
  doughnutChartData: MultiDataSet = [];
  doughnutChartType: ChartType = "doughnut";
  doughnutChartLegend = true;
  doughnutChartColors: Color[] = [];
  doughnutChartOptions: ChartOptions = {
    title: {
      display: false,
      text: "",
      position: "top",
    },
    responsive: true,
    legend: {
      display: true,
      position: "right",
      fullWidth: true,
      reverse: false,
      labels: {
        fontColor: "#333333",
        fontSize: 15,
        fontFamily: "Arial",
      },
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          // get the data label and data value to display
          let dataLabel = data.labels[tooltipItem.index] as string;
          // convert the data value to local string so it uses a comma seperated number
          const value = ` (${data.datasets[tooltipItem.datasetIndex].data[
            tooltipItem.index
          ].toLocaleString()}`;

          // Put suffix on label
          dataLabel += `${value}%)`;

          return dataLabel;
        },
      },
    },
  };
  doughnutChartPlugins: PluginServiceGlobalRegistrationAndOptions[] = [
    {
      // afterDraw: (chart: Chart | any) => {

      // },
      beforeDraw: (chart: Chart | any) => {
        // Get ctx from string
        const ctx = chart.ctx;
      },
    },
  ];
  // END - turnoverDoughnut chart

  // BEGIN - companiesLine chart
  lineChartLabels: Label[] = [];
  lineChartData: ChartDataSets[] = [];
  lineChartType: ChartType = "line";
  lineChartLegend = true;
  lineChartColors: Color[] = [];
  lineChartOptions: ChartOptions = {
    title: {
      display: false,
      text: "",
      position: "top",
    },
    responsive: true,
    legend: {
      display: true,
      position: "top",
      fullWidth: true,
      reverse: false,
      labels: {
        fontColor: "#333333",
        fontSize: 15,
        fontFamily: "Arial",
      },
    },
    scales: {
      xAxes: [
        {
          gridLines: {
            color: "rgba(0, 0, 0, 0.05)",
          },
        },
      ],
      yAxes: [
        {
          gridLines: {
            color: "rgba(0, 0, 0, 0.05)",
          },
        },
      ],
    },
  };
  lineChartPlugins: PluginServiceGlobalRegistrationAndOptions[] = [];
  // END - companiesLine chart

  loggedUserRoleType = "";
  usersCount: number;
  companiesCount: number;
  propertiesCount: number;
  subscriptionsCount: number;
  accessLogsCount: number;
  deadlinesCount: number;
  tanksCount: number;
  caretakersCount: number;
  temporariesCount: number;

  totalDocs = {
    totMandatory: 0,
    totInsert: 0,
    totNotInsert: 0,
    totOptional: 0,
  };
  progressBarVal = 0;
  progressBarValStyle = { width: "0px" };

  subscriptionsRevenue = {
    full: 0,
    fullPerc: 0,
    fullbasic: 0,
    fullbasicPerc: 0,
    free: 0,
    freePerc: 0,
    freebasic: 0,
    freebasicPerc: 0,
    test: 0,
    testPerc: 0,
    contratti: 0,
    contrattiPerc: 0,
    checklist: 0,
    checklistPerc: 0,
    pacchettofisso: 0,
    pacchettofissoPerc: 0,
    nfcAccess: 0,
    nfcAccessPerc: 0,
    qrsicurezza: 0,
    qrsicurezzaPerc: 0,
  };
  totalSubscriptionsRevenue = 0;
  totDocs: number;
  tankDocumentsCount = 0;

  companyId: string;
  disclaimer: any = {};

  lastInsertedDocs = 0;

  isCompaniesException = false;

  isSuppliersReports = false;

  isNewSuppliersInTank = false;

  masterShowPropertiesArea = false;
  masterShowSuppliersArea = false;

  serverDiskUsed = 0;

  /**
   * Class constructor
   */
  constructor(
    private themeService: ThemeService,
    private authService: AuthService,
    private router: Router,
    private translateService: TranslateService,
    private usersService: UsersService,
    private companiesService: CompaniesService,
    private propertiesService: PropertiesService,
    private accessLogsService: AccessLogsService,
    private helperService: HelperService,
    private deadlinesService: DeadlinesService,
    private dialog: MatDialog,
    private documentArchivesService: DocumentArchivesService,
    private tankService: TankService,
    private caretakersService: CaretakersService,
    private configService: ConfigService,
    private suppliersService: SuppliersService,
    private utilsService: UtilsService,
    private documentArchiveDocsService: DocumentArchiveDocsService
  ) {}

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle("Home");

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => {
        this.loggedUser = res;
      })
    );

    this.subscriptions.push(
      this.authService.loggedUserRequestedRoleObj$.subscribe((res) => {
        // Activate loading spinners
        this.usersCount = undefined;
        this.companiesCount = undefined;
        this.accessLogsCount = undefined;
        this.deadlinesCount = undefined;
        this.propertiesCount = undefined;
        this.subscriptionsCount = undefined;
        this.tanksCount = undefined;
        this.caretakersCount = undefined;
        this.temporariesCount = undefined;

        if (res.type) {
          this.loggedUserRoleType = res.type;
        }
        // Temporary users have no dashboard: the properties list is their
        // landing page. replaceUrl keeps the empty home out of the history.
        if (this.loggedUserRoleType === "temporary") {
          const lang =
            this.translateService.currentLang || this.translateService.defaultLang;
          this.router.navigate([lang, "app", "properties", "list"], { replaceUrl: true });
          return;
        }
        if (
          this.loggedUserRoleType === "master" ||
          this.loggedUserRoleType === "collaborator"
        ) {
          // Check if correct role is set into req headers
          if (res.company && res.company._id) {
            this.companyId = res.company._id;

            if (
              this.configService.companiesException.includes(
                this.companyId.toString()
              )
            ) {
              this.isCompaniesException = true;
            } else {
              this.isCompaniesException = false;
            }

            this.subscriptions.push(
              this.companiesService
                .get(this.companyId)
                .subscribe((res: any) => {
                  if (res.status) {
                    if (res.data && res.data.subscription) {
                      if (res.data.subscription.suppliers) {
                        this.masterShowSuppliersArea = true;
                      }

                      if (res.data.subscription.documentUpload) {
                        this.masterShowPropertiesArea = true;
                      }
                    }

                    this.buildTabsData();
                  }
                })
            );

            // NEW-M21: with the switch of the 3.0.0 archive on, the totals of the new archive, with the
            // same four names; the legacy route reads the other archive, frozen at the switch
            const totalDocs$: Observable<any> = isArchiveV3Live()
              ? this.documentArchiveDocsService.companyTotals()
              : this.companiesService.getCompanyTotalDocs(this.companyId);
            this.subscriptions.push(
              totalDocs$
                .subscribe(
                  (res2) => {
                    if (
                      typeof res2.data !== "undefined" &&
                      typeof res2.data.totMandatory !== "undefined" &&
                      typeof res2.data.totInsert !== "undefined" &&
                      typeof res2.data.totNotInsert !== "undefined" &&
                      typeof res2.data.totOptional !== "undefined"
                    ) {
                      this.totalDocs = {
                        totMandatory: res2.data.totMandatory,
                        totInsert: res2.data.totInsert,
                        totNotInsert: res2.data.totNotInsert,
                        totOptional: res2.data.totOptional,
                      };
                      this.progressBarVal = 0;
                      if (
                        this.totalDocs.totInsert + this.totalDocs.totNotInsert >
                        0
                      ) {
                        this.progressBarVal = Math.trunc(
                          (this.totalDocs.totInsert /
                            (this.totalDocs.totInsert +
                              this.totalDocs.totNotInsert)) *
                            100
                        );
                      }

                      this.progressBarValStyle = {
                        width: `${this.progressBarVal}%`,
                      };

                      this.totDocs =
                        this.totalDocs.totOptional + this.totalDocs.totInsert;

                      // this.progressBarVal = Math.ceil((res2.items.totMandatory - res2.items.totNotInsert) / res2.items.totMandatory);
                      // this.progressBarValStyle = { 'width': `${this.progressBarVal}%` };
                    }
                    return;
                  },
                  (err) => {
                    this.helperService.handleError(err);
                  }
                )
            );
            const search = {
              endDate: moment().startOf("day").toISOString(),
            };
            if (isArchiveV3Live()) {
              // NEW-M15-SW: the number of the card from the deadlines of the new archive, from today;
              // the legacy route answers 410 once the old archive is switched off
              this.subscriptions.push(
                this.documentArchiveDocsService
                  .deadlines({ start: 0, limit: 1, sort: "endDate", sortVersus: "asc" })
                  .subscribe(
                    (res2) => {
                      if (res2.data && typeof res2.data.totalCount === "number") {
                        this.deadlinesCount = res2.data.totalCount;
                      }
                    },
                    (err) => {
                      this.deadlinesCount = 0;
                      this.helperService.handleError(err);
                    }
                  )
              );
            } else {
              this.subscriptions.push(
                this.deadlinesService
                  .getDocumentsList(0, 10, "endDate", "asc", search)
                  .subscribe((res2) => {
                    if (
                      res2.data &&
                      res2.data.items &&
                      Array.isArray(res2.data.items)
                    ) {
                      this.deadlinesCount = res2.data.totalCount;
                    }
                  })
              );
            }
            // this.buildTabsData();

            // this.dialog.open(ConfirmDialogComponent, {
            //   data: {
            //     title: "Chiusura estiva",
            //     message: `Vi informiamo che i nostri uffici resteranno chiusi dal 01/08 al 30/08. I servizi della piattaforma rimarranno regolarmente attivi per tutto il periodo, tranne l'inserimento dei documenti tramite il serbatoio, che riprenderà il primo venerdì di settembre.\n\nIl team di Archedi Vi ringrazia e Vi augura buone vacanze`,
            //     btnOkText: this.translateService.instant("common.cta.close"),
            //     btnCancelText: this.translateService.instant("common.cta.close"),
            //     messageOnly: true,
            //   },
            //   width: '500px',
            // });
            if (
              typeof this.loggedUser.hideHomePopup === "undefined" ||
              !this.loggedUser.hideHomePopup
            ) {
              const msgDialog =
                "Gentile utente,<br><br>Da oggi è attiva una nuova interfaccia per la ricerca dei documenti, che interessa la ricerca generale in tutti gli immobili e quella più specifica nel singolo immobile. Più semplice e intuitiva, da oggi ricercare uno specifico documento diventa più facile e veloce; ti invitiamo a prendere dimestichezza con le nuove implementazioni, per qualsiasi dubbio non esitare a contattarci.<br><br>Il Team di Archedi<br><br>";
              const dialogRef = this.dialog.open(InfoDialogComponent, {
                data: {
                  title: "LA NUOVA RICERCA È ONLINE!",
                  message: msgDialog,
                  btnCancelText:
                    this.translateService.instant("common.cta.close"),
                  showBnt: true,
                  chekboxShowAgain: true,
                },
                width: "800px",
              });
              dialogRef.afterClosed().subscribe((res) => {
                if (res && res.notShowAgain) {
                  this.subscriptions.push(
                    this.usersService
                      .updateFields(this.loggedUser._id, {
                        hideHomePopup: true,
                      })
                      .subscribe(
                        (res) => {
                          // this.loadPage();
                          // this.eventDeactivate.emit(true);
                        },
                        (err) => {
                          this.helperService.handleError(err);
                        }
                      )
                  );
                }
              });
            }
          }
        }
        if (
          this.loggedUserRoleType === "admin" ||
          this.loggedUserRoleType === "operator"
        ) {
          // Get tankDocumentsCount data
          this.subscriptions.push(
            this.authService.tankDocumentsCount$.subscribe((res) => {
              this.tankDocumentsCount = res;
            })
          );

          this.subscriptions.push(
            this.deadlinesService.getSubscriptionsList().subscribe((res2) => {
              if (
                res2.data &&
                res2.data.items &&
                Array.isArray(res2.data.items)
              ) {
                this.subscriptionsCount = res2.data.totalCount;
              }
            })
          );

          this.subscriptions.push(
            this.propertiesService.getSubscriptionsRevenue().subscribe(
              (res) => {
                if (res && res.data.items) {
                  this.subscriptionsRevenue.full = res.data.items.full;
                  this.subscriptionsRevenue.free = res.data.items.free;
                  this.subscriptionsRevenue.fullbasic =
                    res.data.items.fullbasic;
                  this.subscriptionsRevenue.freebasic =
                    res.data.items.freebasic;
                  // this.subscriptionsRevenue.test = res.data.items.test;
                  // this.subscriptionsRevenue.contratti = res.data.items.contratti;
                  // this.subscriptionsRevenue.checklist = res.data.items.checklist;
                  // this.subscriptionsRevenue.pacchettofisso =
                  //   res.data.items.pacchettofisso;
                  // this.subscriptionsRevenue.nfcAccess = res.data.items.nfcAccess;
                  // this.subscriptionsRevenue.qrsicurezza = res.data.items.qrsicurezza;

                  this.totalSubscriptionsRevenue =
                    this.subscriptionsRevenue.full +
                    this.subscriptionsRevenue.free +
                    this.subscriptionsRevenue.fullbasic +
                    this.subscriptionsRevenue.freebasic +
                    this.subscriptionsRevenue.test +
                    this.subscriptionsRevenue.contratti +
                    this.subscriptionsRevenue.checklist +
                    this.subscriptionsRevenue.pacchettofisso +
                    this.subscriptionsRevenue.nfcAccess +
                    this.subscriptionsRevenue.qrsicurezza;

                  this.subscriptionsRevenue.fullPerc =
                    (this.subscriptionsRevenue.full /
                      this.totalSubscriptionsRevenue) *
                    100;
                  this.subscriptionsRevenue.freePerc =
                    (this.subscriptionsRevenue.free /
                      this.totalSubscriptionsRevenue) *
                    100;
                  this.subscriptionsRevenue.fullbasicPerc =
                    (this.subscriptionsRevenue.fullbasic /
                      this.totalSubscriptionsRevenue) *
                    100;
                  this.subscriptionsRevenue.freebasicPerc =
                    (this.subscriptionsRevenue.freebasic /
                      this.totalSubscriptionsRevenue) *
                    100;
                  // this.subscriptionsRevenue.testPerc =
                  //   (this.subscriptionsRevenue.test /
                  //     this.totalSubscriptionsRevenue) *
                  //   100;
                  // this.subscriptionsRevenue.contrattiPerc =
                  //   (this.subscriptionsRevenue.contratti /
                  //     this.totalSubscriptionsRevenue) *
                  //   100;
                  // this.subscriptionsRevenue.checklistPerc =
                  //   (this.subscriptionsRevenue.checklist /
                  //     this.totalSubscriptionsRevenue) *
                  //   100;
                  // this.subscriptionsRevenue.pacchettofissoPerc =
                  //   (this.subscriptionsRevenue.pacchettofisso /
                  //     this.totalSubscriptionsRevenue) *
                  //   100;
                  // this.subscriptionsRevenue.nfcAccessPerc =
                  //   (this.subscriptionsRevenue.nfcAccess /
                  //     this.totalSubscriptionsRevenue) *
                  //   100;
                  // this.subscriptionsRevenue.qrsicurezzaPerc =
                  //   (this.subscriptionsRevenue.qrsicurezza /
                  //     this.totalSubscriptionsRevenue) *
                  //   100;

                  this.subscriptionsRevenue.fullPerc = Number.parseFloat(
                    this.subscriptionsRevenue.fullPerc.toFixed(2)
                  );
                  this.subscriptionsRevenue.freePerc = Number.parseFloat(
                    this.subscriptionsRevenue.freePerc.toFixed(2)
                  );
                  this.subscriptionsRevenue.fullbasicPerc = Number.parseFloat(
                    this.subscriptionsRevenue.fullbasicPerc.toFixed(2)
                  );
                  this.subscriptionsRevenue.freebasicPerc = Number.parseFloat(
                    this.subscriptionsRevenue.freebasicPerc.toFixed(2)
                  );
                  // this.subscriptionsRevenue.testPerc = Number.parseFloat(
                  //   this.subscriptionsRevenue.testPerc.toFixed(2)
                  // );
                  // this.subscriptionsRevenue.contrattiPerc = Number.parseFloat(
                  //   this.subscriptionsRevenue.contrattiPerc.toFixed(2)
                  // );
                  // this.subscriptionsRevenue.checklistPerc = Number.parseFloat(
                  //   this.subscriptionsRevenue.checklistPerc.toFixed(2)
                  // );
                  // this.subscriptionsRevenue.pacchettofissoPerc =
                  //   Number.parseFloat(
                  //     this.subscriptionsRevenue.pacchettofissoPerc.toFixed(2)
                  //   );
                  // this.subscriptionsRevenue.nfcAccessPerc = Number.parseFloat(
                  //   this.subscriptionsRevenue.nfcAccessPerc.toFixed(2)
                  // );
                  // this.subscriptionsRevenue.qrsicurezzaPerc = Number.parseFloat(
                  //   this.subscriptionsRevenue.qrsicurezzaPerc.toFixed(2)
                  // );
                }
                this.buildTabsData();
              },
              (err) => {
                console.warn(err);
              }
            )
          );

          // Suppliers reports
          this.suppliersService
            .getImportList(0, 2, "date", "desc", "")
            .subscribe((result: ListApiResponseV2) => {
              if (result.data && Array.isArray(result.data)) {
                const data: any[] = result.data;
                for (const el of data) {
                  if (
                    moment(el.date).isValid() &&
                    moment(el.date).diff(moment(), "days") === 0
                  ) {
                    this.isSuppliersReports = true;
                  }
                }
              }
            });

          // New Suppliers in Tank
          this.suppliersService
            .getNewSuppliersInTank(0, 2, "createdAt", "desc", "")
            .subscribe((result: ListApiResponseV2) => {
              if (
                result.data &&
                Array.isArray(result.data) &&
                result.data.length
              ) {
                this.isNewSuppliersInTank = true;
              }
            });

          // Utils
          this.utilsService.getServerDiskSpace().subscribe((res) => {
            if (res.status) {
              this.serverDiskUsed = res.data.used;
            }
          });
        }
      })
    );

    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe((res) => {
        if (res.indexOf("canViewLatestDocsInsList") >= 0) {
          this.subscriptions.push(
            this.documentArchivesService
              .checkHistoricalDocs()
              .subscribe(
                (res) => {
                  if (res.status) {
                    this.lastInsertedDocs = res.data.totalCount;
                  }
                },
                () => {
                  // Only an alert: a refused or failed count shows none
                  this.lastInsertedDocs = 0;
                }
              )
          );
        } else {
          this.lastInsertedDocs = 0;
        }
      })
    );
  }

  /**
   * Build appropriate content for dashboard tabs depending on current logged role type
   *
   * @since 1.0.0
   */
  buildTabsData(): void {
    if (this.loggedUserRoleType === "shared") {
      const lang =
        this.translateService.currentLang || this.translateService.defaultLang;
      this.router.navigate([lang, "app", "properties", "my-property"]);
      return;
    }

    if (this.loggedUserRoleType === "temporary") {
      const lang =
        this.translateService.currentLang || this.translateService.defaultLang;

      this.router.navigate([lang, "app", "properties", "list"]);
      return;
    }
    if (this.loggedUserRoleType === "collaborator") {
      const lang =
        this.translateService.currentLang || this.translateService.defaultLang;
      if (this.masterShowPropertiesArea) {
        this.router.navigate([lang, "app", "properties", "list"]);
      } else {
        // this.router.navigate([lang, "app", "companies", "my-company"]);
        this.router.navigate([lang, "app"]);
      }
      return;
    }

    // Reset chart's data
    this.doughnutChartLabels = [];
    this.doughnutChartColors = [];
    this.doughnutChartData = [];
    this.lineChartLabels = [];
    this.lineChartData = [];
    this.lineChartColors = [];

    if (
      this.loggedUserRoleType === "admin" ||
      this.loggedUserRoleType === "operator"
    ) {
      // Doughnut Chart
      this.doughnutChartLabels.push(this.translateService.instant("home.full"));
      this.doughnutChartLabels.push(this.translateService.instant("home.free"));
      this.doughnutChartLabels.push(
        this.translateService.instant("home.fullbasic")
      );
      this.doughnutChartLabels.push(
        this.translateService.instant("home.freebasic")
      );
      // this.doughnutChartLabels.push(this.translateService.instant("home.test"));
      // this.doughnutChartLabels.push(
      //   this.translateService.instant("home.contracts")
      // );
      // this.doughnutChartLabels.push(
      //   this.translateService.instant("home.checklist")
      // );
      // this.doughnutChartLabels.push(this.translateService.instant('home.fixedPackage'));
      // this.doughnutChartLabels.push(
      //   this.translateService.instant("home.qrSecurity")
      // );
      // this.doughnutChartLabels.push(
      //   this.translateService.instant("home.nfcAccess")
      // );

      this.doughnutChartColors.push({
        backgroundColor: [
          "#EF5350",
          "#EC407A",
          "#AB47BC",
          "#7E57C2",
          // "#5C6BC0",
          // "#42A5F5",
          // '#29B6F6',
          // "#26C6DA",
        ],
      });
      const chartValues = [];
      chartValues.push(this.subscriptionsRevenue.fullPerc);
      chartValues.push(this.subscriptionsRevenue.freePerc);
      chartValues.push(this.subscriptionsRevenue.fullbasicPerc);
      chartValues.push(this.subscriptionsRevenue.freebasicPerc);
      // chartValues.push(this.subscriptionsRevenue.testPerc);
      // chartValues.push(this.subscriptionsRevenue.contrattiPerc);
      // chartValues.push(this.subscriptionsRevenue.checklistPerc);
      // chartValues.push(this.subscriptionsRevenue.pacchettofissoPerc);
      // chartValues.push(this.subscriptionsRevenue.nfcAccessPerc);
      // chartValues.push(this.subscriptionsRevenue.qrsicurezzaPerc);
      this.doughnutChartData.push(chartValues);

      // Line chart
      this.lineChartLabels.push(
        moment().subtract(11, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(10, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(9, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(8, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(7, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(6, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(5, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(4, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(3, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(2, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(1, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(moment().format("MMM YYYY"));

      this.lineChartData.push({
        label: this.translateService.instant("home.full"),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      });
      this.lineChartData.push({
        label: this.translateService.instant("home.free"),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      });
      this.lineChartData.push({
        label: this.translateService.instant("home.fullbasic"),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      });
      this.lineChartData.push({
        label: this.translateService.instant("home.freebasic"),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      });
      // this.lineChartData.push({
      //   label: this.translateService.instant("home.test"),
      //   data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      // });
      // this.lineChartData.push({
      //   label: this.translateService.instant("home.contracts"),
      //   data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      // });
      // this.lineChartData.push({
      //   label: this.translateService.instant("home.checklist"),
      //   data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      // });
      // this.lineChartData.push({
      //   label: this.translateService.instant('home.fixedPackage'),
      //   data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      // });
      // this.lineChartData.push({
      //   label: this.translateService.instant('home.qrFire'),
      //   data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      // });
      // this.lineChartData.push({
      //   label: this.translateService.instant("home.qrSecurity"),
      //   data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      // });
      this.lineChartColors.push({
        backgroundColor: "rgba(255, 255, 255, 0)",
        borderColor: "#EF5350",
      });
      this.lineChartColors.push({
        backgroundColor: "rgba(255, 255, 255, 0)",
        borderColor: "#EC407A",
      });
      this.lineChartColors.push({
        backgroundColor: "rgba(255, 255, 255, 0)",
        borderColor: "#AB47BC",
      });
      this.lineChartColors.push({
        backgroundColor: "rgba(255, 255, 255, 0)",
        borderColor: "#7E57C2",
      });
      // this.lineChartColors.push({
      //   backgroundColor: "rgba(255, 255, 255, 0)",
      //   borderColor: "#5C6BC0",
      // });
      // this.lineChartColors.push({
      //   backgroundColor: "rgba(255, 255, 255, 0)",
      //   borderColor: "#42A5F5",
      // });
      // this.lineChartColors.push({
      //   backgroundColor: "rgba(255, 255, 255, 0)",
      //   borderColor: "#29B6F6",
      // });
      // this.lineChartColors.push({
      //   backgroundColor: "rgba(255, 255, 255, 0)",
      //   borderColor: "#26C6DA",
      // });

      this.subscriptions.push(
        this.usersService
          .getList(0, 10, "createdAt", "desc", { email: "" })
          .subscribe(
            (res) => {
              if (res) {
                this.usersCount = res.data.totalCount;
              }
            },
            (err) => {
              console.warn(err);
            }
          )
      );
      this.subscriptions.push(
        this.companiesService
          .getList(0, 10, "createdAt", "desc", { email: "" })
          .subscribe(
            (res) => {
              if (res) {
                this.companiesCount = res.data.totalCount;
              }
            },
            (err) => {
              console.warn(err);
            }
          )
      );
      this.subscriptions.push(
        this.propertiesService
          .getList(0, 10, "createdAt", "desc", { name: "" })
          .subscribe(
            (res) => {
              if (res) {
                this.propertiesCount = res.data.totalCount;
              }
            },
            (err) => {
              console.warn(err);
            }
          )
      );
    }

    if (
      this.loggedUserRoleType === "master" ||
      this.loggedUserRoleType === "collaborator"
    ) {
      this.doughnutChartLabels.push(
        this.translateService.instant("home.missingRequired")
      );
      this.doughnutChartLabels.push(
        this.translateService.instant("home.mandatoryInserted")
      );
      this.doughnutChartLabels.push(
        this.translateService.instant("home.generics")
      );

      this.doughnutChartColors.push({
        backgroundColor: ["#EF5350", "#7E57C2", "#26C6DA"],
      });
      const chartValues = [30, 50, 20];
      this.doughnutChartData.push(chartValues);

      // Line chart
      this.lineChartLabels.push(
        moment().subtract(11, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(10, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(9, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(8, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(7, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(6, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(5, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(4, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(3, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(2, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(
        moment().subtract(1, "months").format("MMM YYYY")
      );
      this.lineChartLabels.push(moment().format("MMM YYYY"));

      this.lineChartData.push({
        label: this.translateService.instant("home.missingRequired"),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        // pointRadius: 1,
        // pointHoverRadius: 1,
      });
      this.lineChartData.push({
        label: this.translateService.instant("home.mandatoryInserted"),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        // pointRadius: 1,
        // pointHoverRadius: 1,
      });
      this.lineChartData.push({
        label: this.translateService.instant("home.generics"),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        // pointRadius: 1,
        // pointHoverRadius: 1,
      });
      this.lineChartColors.push({
        backgroundColor: "rgba(255, 255, 255, 0)",
        borderColor: "#EF5350",
      });
      this.lineChartColors.push({
        backgroundColor: "rgba(255, 255, 255, 0)",
        borderColor: "#EC407A",
      });
      this.lineChartColors.push({
        backgroundColor: "rgba(255, 255, 255, 0)",
        borderColor: "#AB47BC",
      });

      this.subscriptions.push(
        this.tankService
          .getList(0, 10, "createdAt", "desc", { email: "" })
          .subscribe(
            (res) => {
              if (res) {
                this.tanksCount = res.data.totalCount;
              }
            },
            (err) => {
              console.warn(err);
            }
          )
      );
      this.subscriptions.push(
        this.propertiesService
          .getList(0, 10, "createdAt", "desc", { name: "" })
          .subscribe(
            (res) => {
              if (res) {
                this.propertiesCount = res.data.totalCount;
              }
            },
            (err) => {
              console.warn(err);
            }
          )
      );
      this.subscriptions.push(
        this.caretakersService.getCaretakers().subscribe(
          (res) => {
            if (res) {
              this.caretakersCount =
                res &&
                res.data &&
                res.data.dipendenti &&
                res.data.dipendenti.length
                  ? res.data.dipendenti.length
                  : 0;
              this.caretakersCount +=
                res &&
                res.data &&
                res.data.impresePulizia &&
                res.data.impresePulizia.length
                  ? res.data.impresePulizia.length
                  : 0;
            }
          },
          (err) => {
            console.warn(err);
          }
        )
      );
      this.subscriptions.push(
        this.usersService
          .getList(0, 10, "createdAt", "desc", { role: "temporary" })
          .subscribe(
            (res) => {
              if (res) {
                this.temporariesCount = res.data.totalCount;
              }
            },
            (err) => {
              console.warn(err);
            }
          )
      );
    }
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions

    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
